import Cookies from 'js-cookie';
import type * as ld from 'launchdarkly-js-client-sdk';
import React, { createContext, useEffect, useState } from 'react';

import { launchdarklyClientKey } from '@/environment';
import { useAnalytics } from '@/services/analytics';
import { ANONYMOUS_USER, DidUser, useAuth } from '@/services/auth';
import { getTrackIdFromLDContext, setTrackIdOnSharedCookie } from '@/utils/track-id';
import { crossSiteDomain } from '@/utils/utils';
import { PlanGroup } from '@d-id/types';
import { datadogRum } from '@datadog/browser-rum';
import { nanoid } from 'nanoid';

interface Props {
    children: React.ReactNode;
}

const Store = createContext<ld.LDClient | null>({} as ld.LDClient);
Store.displayName = 'feature flags store';

const ldContextCookieKey = 'taki';

function toContext(user: DidUser, hashKey: string, isAnonymous?: boolean): ld.LDSingleKindContext {
    if (!user || isAnonymous) {
        return { kind: 'user', key: ANONYMOUS_USER.sub, hashKey: hashKey, planGroup: PlanGroup.GUEST, anonymous: true };
    } else {
        return { ...user, kind: 'user', key: user.sub, name: user.name ?? 'unknown' };
    }
}

function storeContextInCookies(context: ld.LDSingleKindContext | DidUser) {
    const { key, hashKey, sub, org_id, owner_id, planGroup, createdAt } = context;

    const contextCookieValue = btoa(JSON.stringify({ key, hashKey, sub, org_id, owner_id, planGroup, createdAt }));

    Cookies.set(ldContextCookieKey, contextCookieValue, { domain: crossSiteDomain });
}

export const useFeatureFlag = () => React.useContext(Store);

const getConfiguredHashKey = (user?: DidUser | null, isAnonymous?: boolean) => {
    if (!isAnonymous && user?.hashKey) {
        return user.hashKey;
    }

    const cookieHashKey = Cookies.get('hash_key');

    if (cookieHashKey) {
        return cookieHashKey;
    }

    const newHashKey = nanoid();
    Cookies.set('hash_key', newHashKey, { domain: crossSiteDomain });

    return newHashKey;
};

export const FeatureFlagsProvider = ({ children }: Props) => {
    const { user, isAnonymous } = useAuth();
    const [instance, setInstance] = useState<ld.LDClient | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (instance || !user || loading) {
            return;
        }
        let hashKey = getConfiguredHashKey(user, isAnonymous);

        setLoading(true);
        import('launchdarkly-js-client-sdk').then(async ({ initialize }) => {
            const context = toContext(user, hashKey, isAnonymous);
            storeContextInCookies(context);
            const trackId = getTrackIdFromLDContext(context);
            setTrackIdOnSharedCookie(trackId);
            const client = initialize(launchdarklyClientKey, context, {
                bootstrap: 'localStorage',
                evaluationReasons: true,
                sendEvents: false,
                inspectors: [
                    {
                        type: 'flag-used',
                        name: 'dd-inspector',
                        method: (key: string, detail: ld.LDEvaluationDetail) => {
                            datadogRum.addFeatureFlagEvaluation(key, detail.value);
                        },
                    },
                ],
            });
            await client.waitUntilReady();

            setInstance(client);
            setLoading(false);
        });
    }, [instance, user, isAnonymous]);

    if (!instance) {
        return null;
    }

    return <Store.Provider value={instance}>{children}</Store.Provider>;
};

export function useFlag<T>(flag: string, defaultValue: T): T {
    return useFeatureFlag()?.variation?.(flag, defaultValue) ?? defaultValue;
}

export function useAgentUiFlag() {
    const { user } = useAuth();

    const cutoff = useFlag('agent_v2_migration_cutoff', Infinity);

    const newUser = new Date(user?.createdAt || Date.now()).getTime() > cutoff;

    return {
        cutoff: newUser,
    };
}

function isTarget(evaluation: ld.LDEvaluationDetail): boolean {
    if (!evaluation.reason) {
        return false;
    } else {
        return ['RULE_MATCH', 'FALLTHROUGH', 'TARGET_MATCH'].includes(evaluation.reason?.kind);
    }
}

const EXPERIMENTS = 'experiments';

type UseFlagExperimentOptions = {
    useCache?: boolean; // NOTICE: This might cause issues when switching users on the same browser
    skipAnalytics?: boolean;
};

export function useFlagExperiment<T>(flag: string, defaultValue: T, options?: UseFlagExperimentOptions): T {
    const client = useFeatureFlag();
    const mixpanel = useAnalytics();
    const { user, isAnonymous } = useAuth();
    const activeExperiments = JSON.parse(Cookies.get(EXPERIMENTS) || '{}');

    const experimentKey = `${flag}#${user && !isAnonymous ? user.sub : ANONYMOUS_USER.sub}`;
    if (options?.useCache && activeExperiments[experimentKey]) {
        return activeExperiments[experimentKey];
    }

    const evaluation = client?.variationDetail?.(flag, defaultValue);
    const value = evaluation?.value ?? defaultValue;
    const variant = JSON.stringify(value);
    if (evaluation?.reason && isTarget(evaluation) && activeExperiments[experimentKey] != variant) {
        if (!options?.skipAnalytics) {
            mixpanel?.track('$experiment_started', {
                'Experiment name': flag,
                'Variant name': variant,
                Reason: evaluation.reason?.kind,
            });
        }
        mixpanel?.enrich({ [flag]: variant });
        Cookies.set(EXPERIMENTS, JSON.stringify({ ...activeExperiments, [experimentKey]: variant }), { domain: crossSiteDomain });
    }
    return value;
}

export function useGetAllFlags() {
    const client = useFeatureFlag();
    return client?.allFlags();
}
